/**
 * Hand loan interest — rate is always **rupees per ₹100 principal per month**.
 *
 * Short-term daily shop flow (example: ₹1,00,000 @ ₹3/₹100/mo):
 *   interest/day = (principal × rate ÷ 100) ÷ 30  → ₹100/day
 *   Hold 1 night → pay ₹100 interest, then close with principal ₹1,00,000
 *   Same-day return → ₹0 interest, close with principal only
 *
 * EMI (monthly collections — same as Finance):
 *   total interest = principal × (rate ÷ 100) × months
 *   EMI            = (principal + total interest) ÷ months
 */
import { STANDARD_DAYS_PER_MONTH, totalDaysAcrossMonths } from './rupeeInterest';

export type HandLoanRepaymentType = 'short_term' | 'emi';

export function parseHandLoanRepaymentType(v: unknown, legacyInterestBasis?: unknown): HandLoanRepaymentType {
  if (v === 'short_term' || v === 'daily') return 'short_term';
  if (v === 'emi' || v === 'monthly') return 'emi';
  return legacyInterestBasis === 'daily' ? 'short_term' : 'emi';
}

export function handLoanRepaymentLabel(t: HandLoanRepaymentType): string {
  return t === 'short_term' ? 'Short term (pay by days)' : 'EMI (monthly collections)';
}

export const HAND_LOAN_RATE_LABEL = 'Interest (₹ per ₹100 principal / month)';

export function interestPerMonth(principal: number, rate: number): number {
  if (!Number.isFinite(principal) || principal <= 0 || !Number.isFinite(rate) || rate <= 0) return 0;
  return Math.round(principal * (rate / 100));
}

export function interestPerDay(principal: number, rate: number): number {
  const monthly = interestPerMonth(principal, rate);
  if (!monthly) return 0;
  return Math.round(monthly / STANDARD_DAYS_PER_MONTH);
}

export type ShortTermHandLoanCalc = {
  principal: number;
  rate: number;
  days: number;
  daysInMonthUsed: number;
  interestPerMonth: number;
  interestPerDay: number;
  totalInterest: number;
  totalPayable: number;
};

export function computeShortTermHandLoan(
  principal: number,
  rate: number,
  days: number
): ShortTermHandLoanCalc | null {
  if (!Number.isFinite(principal) || principal <= 0) return null;
  if (!Number.isFinite(rate) || rate < 0) return null;
  const termDays = Math.max(0, Math.round(Number(days) || 0));
  const perMonth = interestPerMonth(principal, rate);
  const perDay = perMonth > 0 ? Math.round(perMonth / STANDARD_DAYS_PER_MONTH) : 0;
  const totalInterest = Math.round(perDay * termDays);
  return {
    principal,
    rate,
    days: termDays,
    daysInMonthUsed: STANDARD_DAYS_PER_MONTH,
    interestPerMonth: perMonth,
    interestPerDay: perDay,
    totalInterest,
    totalPayable: principal + totalInterest,
  };
}

export type EmiHandLoanCalc = {
  principal: number;
  rate: number;
  months: number;
  interestPerMonth: number;
  totalInterest: number;
  totalPayable: number;
  emi: number;
};

export function computeEmiHandLoan(principal: number, rate: number, months: number): EmiHandLoanCalc | null {
  if (!Number.isFinite(principal) || principal <= 0) return null;
  if (!Number.isFinite(rate) || rate < 0) return null;
  const termMonths = Math.max(0, Math.round(Number(months) || 0));
  if (!termMonths) return null;
  const perMonth = interestPerMonth(principal, rate);
  const totalInterest = Math.round(perMonth * termMonths);
  const totalPayable = principal + totalInterest;
  const emi = Math.round(totalPayable / termMonths);
  return { principal, rate, months: termMonths, interestPerMonth: perMonth, totalInterest, totalPayable, emi };
}

/** Legacy records: term stored as months — convert to days (months × 30). */
export function resolveShortTermDays(
  loanTerm: unknown,
  repaymentType: HandLoanRepaymentType,
  legacyInterestBasis: unknown,
  _startDate = new Date()
): number {
  const raw = Math.max(1, Math.round(parseFloat(String(loanTerm ?? '')) || 30));
  if (repaymentType === 'short_term' && legacyInterestBasis !== 'daily') return raw;
  if (legacyInterestBasis === 'daily' && repaymentType === 'short_term') {
    return totalDaysAcrossMonths(raw);
  }
  return raw;
}

export function formatInr(amount: number): string {
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
}
